import React from "react";
import { useIntl } from "react-intl";
import { AxiosError } from "axios";

import { type MachineGroupConfiguration } from "@/schemas";
import { useUpstreamMachines } from "@/hooks/machines/use-upstream-machines";
import type { Machine } from "@/schemas/machine";

import { MachineGroupsConfigurationModalTemplate } from "./machine-groups-configuration-modal.template";

export interface MachineGroupsConfigurationModalProps {
	readonly open: boolean;
	readonly defaultValues?: MachineGroupConfiguration & { id?: string };
	readonly onSubmit: (data: MachineGroupConfiguration) => Promise<void>;
	readonly onClose: () => void;
	readonly error: Error | null;
}

export const MachineGroupsConfigurationModal: React.FC<MachineGroupsConfigurationModalProps> = ({
	open,
	onClose,
	onSubmit,
	defaultValues,
	error,
}) => {
	const intl = useIntl();
	const { machines } = useUpstreamMachines(false);

	const getErrorMessage = (error: Error | null): string | undefined => {
		if (error === null) {
			return undefined;
		}

		if (error instanceof AxiosError && error.status === 409) {
			switch (error.response?.data) {
				case "UniqueTenantAlias":
					return intl.formatMessage({
						id: "Failed to save Machine Group: An item already exists with this name",
						defaultMessage: "Failed to save Machine Group: An item already exists with this name",
					});
				case "NoClosingMachinesConstraint":
					return intl.formatMessage({
						id: "Failed to save Machine Group: No closing machines allowed in a machine group",
						defaultMessage: "No closing machines allowed in a machine group",
					});
				case "UniqueConfiguredMachineIds":
					return intl.formatMessage({
						id: "Failed to save Machine Group: A selected machine already belongs to another Machine Group",
						defaultMessage: "Failed to save Machine Group: A selected machine already belongs to another Machine Group",
					});
				case "X7MachineGroupConstraint":
					return intl.formatMessage({
						id: "An X7 can be the only machine configured in a Machine Group",
						defaultMessage: "An X7 can be the only machine configured in a Machine Group",
					});
				default:
					break;
			}
		}

		return intl.formatMessage({
			id: "Failed to save Machine Group",
			defaultMessage: "Failed to save Machine Group",
		});
	};

	return (
		<MachineGroupsConfigurationModalTemplate
			open={open}
			onClose={onClose}
			onSubmit={onSubmit}
			defaultValues={defaultValues}
			machineList={(machines ?? []) as Machine[]}
			errorMessage={getErrorMessage(error)}
		/>
	);
};
